/**
 * TradeModal — shared base class for all trade portal modals.
 *
 * Handles open/close, backdrop click, escape key, focus trapping,
 * focus restore, and body scroll lock. Subclasses only need to
 * implement their own content-specific logic.
 *
 * Usage (standalone):
 *   const modal = new TradeModal(document.getElementById('my-modal'));
 *   modal.open();
 *
 * Usage (subclass):
 *   class MyModal extends TradeModal {
 *     onOpen()  { … }   // called after modal is visible
 *     onClose() { … }   // called after modal is hidden
 *   }
 */
class TradeModal {
	constructor(overlay, options) {
		const opts = options || {};
		this.overlay = overlay;
		this.content = overlay.querySelector('.modal-content');
		this.triggerEl = null;
		this._autoCloseTimer = null;
		this._boundKeyDown = this._onKeyDown.bind(this);

		if (opts.closeSelector !== false) {
			this._bindCloseButton(opts.closeSelector || '[data-modal-close]');
		}

		this._bindOverlayClick();
	}

	/* ── public API ── */

	open(triggerEl) {
		this.triggerEl = triggerEl || document.activeElement;
		this.overlay.classList.remove('modal-overlay--hidden');
		this.overlay.setAttribute('aria-hidden', 'false');
		document.body.classList.add('trade-modal-open');
		document.addEventListener('keydown', this._boundKeyDown);
		this._trapFocus();

		if (typeof this.onOpen === 'function') this.onOpen();
	}

	close() {
		this.overlay.classList.add('modal-overlay--hidden');
		this.overlay.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('trade-modal-open');
		document.removeEventListener('keydown', this._boundKeyDown);

		if (this._autoCloseTimer) {
			clearTimeout(this._autoCloseTimer);
			this._autoCloseTimer = null;
		}

		if (this.triggerEl && typeof this.triggerEl.focus === 'function') {
			this.triggerEl.focus();
		}

		if (typeof this.onClose === 'function') this.onClose();
	}

	get isOpen() {
		return !this.overlay.classList.contains('modal-overlay--hidden');
	}

	/* ── focus trap ── */

	_getFocusableEls() {
		return this.content.querySelectorAll(
			'a[href], button:not([disabled]), textarea:not([disabled]), ' +
				'input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
				'[tabindex]:not([tabindex="-1"])',
		);
	}

	_trapFocus() {
		const focusable = this._getFocusableEls();
		if (focusable.length) {
			focusable[0].focus();
		}
	}

	_handleTab(e) {
		const focusable = this._getFocusableEls();
		if (!focusable.length) return;

		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if (e.shiftKey) {
			if (document.activeElement === first) {
				e.preventDefault();
				last.focus();
			}
		} else {
			if (document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		}
	}

	/* ── event handlers ── */

	_onKeyDown(e) {
		if (e.key === 'Escape') {
			this.close();
		} else if (e.key === 'Tab') {
			this._handleTab(e);
		}
	}

	_bindCloseButton(selector) {
		this.overlay.addEventListener('click', (e) => {
			if (e.target.closest(selector)) {
				this.close();
			}
		});
	}

	_bindOverlayClick() {
		this.overlay.addEventListener('click', (e) => {
			if (e.target === this.overlay) {
				this.close();
			}
		});
	}
}

window.TradeModal = TradeModal;
